import type { ExpiringAgreementModel } from '@kk/api/modules/leasing';
import {
  type QueryClient,
  type UseQueryOptions,
  useQuery,
} from '@tanstack/react-query';
import { leasingApi } from '@/api';

const EXPIRING_AGREEMENT_QUERY_KEYS = [
  ['normalExpiringAgreements'],
  ['ayvensExpiringAgreements'],
  ['handledAgreements'],
  ['anonymousNormalExpiringAgreements'],
  ['anonymousAyvensExpiringAgreements'],
] as const;

export function invalidateExpiringAgreementQueries(queryClient: QueryClient) {
  return Promise.all(
    EXPIRING_AGREEMENT_QUERY_KEYS.map((queryKey) =>
      queryClient.invalidateQueries({ queryKey }),
    ),
  );
}

export function useNormalExpiringAgreements(
  options?: Omit<
    UseQueryOptions<ExpiringAgreementModel[], Error>,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery<ExpiringAgreementModel[], Error>({
    queryKey: ['normalExpiringAgreements'],
    queryFn: async () => leasingApi.getApiV1ExpiringagreementsAllnormal(),
    ...options,
  });
}

export function useAyvensExpiringAgreements(
  options?: Omit<
    UseQueryOptions<ExpiringAgreementModel[], Error>,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery<ExpiringAgreementModel[], Error>({
    queryKey: ['ayvensExpiringAgreements'],
    queryFn: async () => leasingApi.getApiV1ExpiringagreementsAllayvens(),
    ...options,
  });
}

export function useHandledAgreements(
  options?: Omit<
    UseQueryOptions<ExpiringAgreementModel[], Error>,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery<ExpiringAgreementModel[], Error>({
    queryKey: ['handledAgreements'],
    queryFn: async () => leasingApi.getApiV1ExpiringagreementsAllhandled(),
    ...options,
  });
}

export function useAnonymousNormalExpiringAgreements(
  paramA: string,
  paramB: string,
  paramP: string,
) {
  return useQuery<ExpiringAgreementModel[], Error>({
    queryKey: ['anonymousNormalExpiringAgreements', paramA, paramB, paramP],
    queryFn: async () =>
      leasingApi.getApiV1ExpiringagreementsAllnormalanoByParamAAndParamBParamP(
        paramA,
        paramB,
        paramP,
      ),
    enabled: !!paramA && !!paramB && !!paramP,
  });
}

export function useAnonymousAyvensExpiringAgreements(
  paramA: string,
  paramB: string,
  paramP: string,
) {
  return useQuery<ExpiringAgreementModel[], Error>({
    queryKey: ['anonymousAyvensExpiringAgreements', paramA, paramB, paramP],
    queryFn: async () =>
      leasingApi.getApiV1ExpiringagreementsAllayvensanoByParamAAndParamBParamP(
        paramA,
        paramB,
        paramP,
      ),
    enabled: !!paramA && !!paramB && !!paramP,
  });
}
